import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import FormattingTooltip from '../basicComponents/tooltip/dollarsTooltip';
import Switcher from '../dealCatalog-page/switcher';
import { Loader } from '../loaders/loader';
import { formatByType } from '../table/anchor-drop-down/infoList/formatSettings';
import SectorsToAvoid from '../table/anchor-drop-down/infoList/sectorsToAvoid';
import { InfoListRow } from '../table/anchor-drop-down/infoList/types';
import { CLIENT_DETAILS_SUMMARY_QUERY } from './table/popovers/entity';
import { IRowData } from './types';

interface InfoListProps {
  allocation: IRowData;
}

export const EntityInfoList = ({ allocation }: InfoListProps) => {
  const [switcher, setSwitcher] = useState(true);

  const { investmentType, assetClass, investmentName } = {
    investmentType: allocation.investment.type,
    assetClass: allocation.investment.assetClass,
    investmentName: allocation.investment.name
  };

  const { data, refetch, loading } = useQuery(CLIENT_DETAILS_SUMMARY_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: allocation.legalEntity.id,
      familyId: allocation.legalEntity.family.name,
      includeRecommendation: switcher,
      limit: 50,
      offset: 0
    }
  });

  useEffect(() => {
    refetch();
  }, []);

  const changeSwitcher = () => {
    setSwitcher(!switcher);
    refetch({
      id: allocation.legalEntity.id,
      familyId: allocation.legalEntity.family.name,
      includeRecommendation: !switcher
    });
  };

  if (data === undefined || loading) {
    return <Loader />;
  }

  const tooltipContent = `${assetClass} corresponds to\n${investmentName}.`;

  const entityPopover: InfoListRow[] = [
    {
      name: 'On Hold',
      formatType: 'yesNo',
      value: data.legalEntity.onHold
    },
    {
      name: 'Always Show',
      formatType: 'yesNo',
      value: data.legalEntity.alwaysShow
    },
    {
      name: 'Name',
      formatType: 'string',
      value: data.legalEntity.entityName
    },
    {
      name: 'Entity Value',
      formatType: 'JSX',
      value: <FormattingTooltip>{data.legalEntity.totalEntityValue}</FormattingTooltip>
    },
    {
      name: '% To Private Capital',
      formatType: 'percentage',
      value: data.legalEntity.privatePercentage
    },
    {
      name: 'Target OC Ratio',
      formatType: 'x',
      value: data.legalEntity.targetOCRatio
    },
    {
      name: investmentType ? `Bite Size (${investmentType?.toLowerCase()})` : 'Bite Size',
      formatType: 'JSX',
      value: <FormattingTooltip>{investmentType === 'Fund' ? data.legalEntity.fundBiteSize : data.legalEntity.directBiteSize}</FormattingTooltip>
    },
    {
      name: 'Sectors To Avoid',
      tooltipName: undefined,
      formatType: 'string',
      value: data.legalEntity.sectorsToAvoid
    }
  ];

  const checkISArrayFilled = (array: string[]) => !!array.filter((item) => item).length;

  const cleanData = (): InfoListRow[] =>
    entityPopover.filter(({ value }) => {
      if (Array.isArray(value)) return checkISArrayFilled(value);
      return value !== null && value !== undefined;
    });

  return (
    <MoreDataWrap>
      <WrapperSwitcher>
        <Switcher isActive={switcher} onClick={changeSwitcher} />
        Include Pending Commitments
      </WrapperSwitcher>
      <ScrollWrap>
        {cleanData().map(({ name, tooltipName, value, formatType }) => (
          <MoreDataItem key={name}>
            <MoreDataItemTitle>
              <p>{name}</p>
              {tooltipName && (
                <Tooltip title={tooltipName}>
                  <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                </Tooltip>
              )}
            </MoreDataItemTitle>
            {Array.isArray(value) ? (
              <SectorsToAvoid list={value} />
            ) : (
              <MoreDataItemValue>{formatByType[formatType](value as never)}</MoreDataItemValue>
            )}
          </MoreDataItem>
        ))}
      </ScrollWrap>
    </MoreDataWrap>
  );
};

const ScrollWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow: scroll;
`;

const WrapperSwitcher = styled.div`
  display: flex;
  column-gap: 10px;
  margin-bottom: 22px;
  color: ${themeVar('font.base')};
`;

const MoreDataItemValue = styled.div`
  display: flex;
  justify-content: center;
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
  text-align: center;
  width: 50%;
  color: ${themeVar('font.base')};
`;

const MoreDataItemTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  width: 50%;
  min-width: 50%;
  color: ${themeVar('font.base')};
`;

const MoreDataItem = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 20px;
  background-color: ${themeVar('layer.1')};
  gap: 10px;
  border-radius: 4px;
`;

const MoreDataWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
